import unescapedText from 'I18n/utils/unescapedText';
import { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { TextArea } from 'foundations-components/transitional/components/TextArea';
import { useApprovalContext } from '../../hooks/useApprovalContext';
import { ApprovalOperation } from '../../types';
import { ACTOR, DEFAULT_AGENT_APPROVER_ID } from '../../constants/actors';
import { parseActor, serializeActor } from 'ui-product-approvals-common/utils/actorKey';
import { ApprovalRulesRadio } from '../ApprovalRulesRadio';
import { ApproverSelect } from '../ApproverSelect';
import { ApprovalModalMessage } from './ApprovalModalMessage';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import { ApprovalModalMessageLabelHelpI18n, ApprovalModalMessageLabelI18n } from '../i18n/ApprovalModalMessageLabelI18n';
import { ApprovalDataErrorMessage } from '../sidebar/ApprovalDataErrorMessage';
import { View } from 'foundations-components/transitional/components/View';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { ApprovalDueDateInputGroup } from '../ApprovalDueDateInputGroup';
import { Loadable } from 'foundations-components/transitional/utils/Loadable';
import { useGetIsUngated } from '../../hooks/useGetIsUngated';
import { GATES } from '../../constants/auth';
import { ApprovalTitleTextInput } from '../ApprovalTitleTextInput';
import { MAX_APPROVAL_NOTE_LENGTH } from '../../constants/requestModal';
import { useApprovers } from '../../hooks/useApprovers';
import { useFetchUsersByScope } from '../../hooks/useFetchUsersByScope';
import { LazyOrderedApprovalStepsBuilder } from '../orderedSteps/LazyOrderedApprovalStepsBuilder';
import { trackInteraction } from '../../utils/usageTracker';
import { APPROVAL_MODAL_DEFAULT_AGENT_REMOVED } from '../../constants/trackingConstants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const FlushUnderLabel = styled.div.withConfig({
  displayName: "ApprovalModalBody__FlushUnderLabel"
})(({
  theme
}) => ({
  marginBlockStart: `-${theme.space['100']}`
}));
const LazyApprovalNoteEditor = Loadable({
  loader: () => import( /* webpackChunkName: "ApprovalNoteEditor" */'../common/ApprovalNoteEditor').then(m => ({
    default: m.ApprovalNoteEditor
  })),
  LoadingComponent: () => /*#__PURE__*/_jsx(LoadingSpinner, {
    layout: "centered",
    size: "small"
  }),
  ErrorComponent: ApprovalDataErrorMessage
});
export const ApprovalModalBody = ({
  alertContent,
  currentOperation,
  approverActors,
  isOrderedApprovalSteps,
  approvalSteps,
  availableApprovalRules,
  isLoading,
  note,
  onChangeApprovers,
  onChangeApprovalRule,
  onChangeNote,
  isNoteTooLong,
  dueDate,
  setDueDate,
  dueTime,
  setDueTime,
  isDueDateValid,
  setIsDueDateValid,
  onNoteOverLimit,
  approvalTitle,
  canEditApprovalTitle,
  onChangeApprovalTitle
}) => {
  var _approval$meta$curren;
  const approval = useApprovalContext();
  const {
    approvalType,
    approvalAction
  } = approval.meta;
  const currentUserId = (_approval$meta$curren = approval.meta.currentUser) === null || _approval$meta$curren === void 0 ? void 0 : _approval$meta$curren.user_id;
  const getHasGate = useGetIsUngated();
  const hasRichTextGate = getHasGate(GATES.REQUEST_NOTE_RICH_TEXT);
  const {
    users: superAdminUsers
  } = useFetchUsersByScope('super-admin', {
    skip: !isOrderedApprovalSteps
  });
  const superAdminUserIds = useMemo(() => new Set(superAdminUsers.map(user => String(user.id))), [superAdminUsers]);
  const onChangeNoteEvent = evt => onChangeNote(evt.target.value);
  const {
    approvers,
    isLoading: isLoadingApprovers,
    isError: isFetchApproversError
  } = useApprovers(approvalType, approvalAction);
  const isRequestOperation = currentOperation === ApprovalOperation.REQUEST;
  const showApprovalMessage = !(isRequestOperation && canEditApprovalTitle);
  const noteIsRequired = useMemo(() => currentOperation === ApprovalOperation.CANCEL || currentOperation === ApprovalOperation.REJECT || currentOperation === ApprovalOperation.REQUEST_CHANGES, [currentOperation]);
  const hasAgentGate = getHasGate(GATES.AGENT_APPROVER);
  const defaultAgentApprover = useMemo(() => approvers.find(approver => approver.actor.actorType === ACTOR.AGENT && approver.actor.id === DEFAULT_AGENT_APPROVER_ID), [approvers]);

  // Add the default agent once per mount, after the approvers finish loading.
  // The modal remounts on every open (ApprovalProvider renders it as
  // `showModal && <ApprovalModal/>`), so it is re-added on reopen for free;
  // the ref only stops the agent being re-added if the requester removes it
  // within the same open. In the ordered flow we also start an empty second
  // step to prompt the requester to add a human approver after the agent.
  const hasAddedDefaultAgentRef = useRef(false);
  useEffect(() => {
    if (hasAddedDefaultAgentRef.current || !isRequestOperation || !hasAgentGate || isLoadingApprovers || !defaultAgentApprover) {
      return;
    }
    hasAddedDefaultAgentRef.current = true;
    approvalSteps.addApprover(approvalSteps.steps[0].id, defaultAgentApprover);
    approvalSteps.addStep();
  }, [isRequestOperation, hasAgentGate, isLoadingApprovers, defaultAgentApprover, approvalSteps]);
  const handleRemoveApprover = (stepId, actorKey) => {
    if (hasAgentGate && parseActor(actorKey).id === DEFAULT_AGENT_APPROVER_ID) {
      trackInteraction(APPROVAL_MODAL_DEFAULT_AGENT_REMOVED, approvalType);
    }
    approvalSteps.removeApprover(stepId, actorKey);
  };
  if (isLoading) {
    return /*#__PURE__*/_jsx(DialogBody, {
      children: /*#__PURE__*/_jsx(TFlex, {
        alignItems: "center",
        justifyContent: "center",
        padding: "300",
        children: /*#__PURE__*/_jsx(LoadingSpinner, {})
      })
    });
  }
  if (isRequestOperation && isLoadingApprovers) {
    return /*#__PURE__*/_jsx(DialogBody, {
      children: /*#__PURE__*/_jsx(TFlex, {
        alignItems: "center",
        justifyContent: "center",
        padding: "300",
        children: /*#__PURE__*/_jsx(LoadingSpinner, {})
      })
    });
  }
  if (isRequestOperation && isFetchApproversError) {
    return /*#__PURE__*/_jsx(DialogBody, {
      marginBlockEnd: "500",
      children: /*#__PURE__*/_jsx(ErrorMessage, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "productApprovals.ApprovalModalBody.fetchError.title"
        }),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "productApprovals.ApprovalModalBody.fetchError.description"
        })
      })
    });
  }
  return /*#__PURE__*/_jsxs(DialogBody, {
    maxBlockSize: "calc(100vh - 260px)",
    overflowY: "auto",
    children: [(alertContent || showApprovalMessage) && /*#__PURE__*/_jsxs(View, {
      "data-test-id": "product-approvals-approval-modal-body",
      marginBlockEnd: "300",
      as: "div",
      children: [alertContent && /*#__PURE__*/_jsx(View, {
        marginBlockEnd: "300",
        as: "div",
        children: alertContent
      }), showApprovalMessage && /*#__PURE__*/_jsx(ApprovalModalMessage, {})]
    }), isRequestOperation && /*#__PURE__*/_jsxs(_Fragment, {
      children: [canEditApprovalTitle && /*#__PURE__*/_jsx(ApprovalTitleTextInput, {
        approvalTitle: approvalTitle,
        onChangeApprovalTitle: onChangeApprovalTitle
      }), !isOrderedApprovalSteps && /*#__PURE__*/_jsx(ApproverSelect, {
        approvers: approvers,
        value: approverActors,
        onChange: onChangeApprovers
      }), availableApprovalRules.length > 1 && /*#__PURE__*/_jsx(ApprovalRulesRadio, {
        approvalType: approval.meta.approvalType,
        onChangeApprovalRule: onChangeApprovalRule,
        isOrderedApprovalSteps: isOrderedApprovalSteps
      }), isOrderedApprovalSteps && /*#__PURE__*/_jsx(FormControl, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "productApprovals.approverSteps"
        }),
        required: true,
        tooltipProps: {
          use: 'longform'
        },
        tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "productApprovals.ApprovalModal.approverTooltip"
        }),
        children: /*#__PURE__*/_jsx(FlushUnderLabel, {
          children: /*#__PURE__*/_jsx(LazyOrderedApprovalStepsBuilder, {
            steps: approvalSteps.steps,
            approvers: approvers,
            superAdminUserIds: superAdminUserIds,
            currentUserActorKey: currentUserId === undefined ? undefined : serializeActor({
              actorType: ACTOR.USER,
              id: String(currentUserId)
            }),
            onAddStep: approvalSteps.addStep,
            onRemoveStep: approvalSteps.removeStep,
            onReorderSteps: approvalSteps.reorderSteps,
            onAddApprover: approvalSteps.addApprover,
            onRemoveApprover: handleRemoveApprover,
            onMoveApprover: approvalSteps.moveApprover
          })
        })
      }), /*#__PURE__*/_jsx(ApprovalDueDateInputGroup, {
        dueDate: dueDate,
        setDueDate: setDueDate,
        dueTime: dueTime,
        setDueTime: setDueTime,
        isDueDateValid: isDueDateValid,
        setIsDueDateValid: setIsDueDateValid
      })]
    }), /*#__PURE__*/_jsx(FormControl, {
      characterCount: hasRichTextGate ? undefined : MAX_APPROVAL_NOTE_LENGTH - note.length,
      help: /*#__PURE__*/_jsx(ApprovalModalMessageLabelHelpI18n, {
        currentOperation: currentOperation,
        approvalType: approval.meta.approvalType
      }),
      label: /*#__PURE__*/_jsx(ApprovalModalMessageLabelI18n, {
        currentOperation: currentOperation,
        approvalType: approval.meta.approvalType
      }),
      required: noteIsRequired,
      validationMessage: isNoteTooLong && /*#__PURE__*/_jsx(FormattedMessage, {
        message: "productApprovals.approvalModalNoteError.exceedsMaxLength",
        options: {
          maxLength: MAX_APPROVAL_NOTE_LENGTH
        }
      }),
      error: isNoteTooLong,
      children: hasRichTextGate ? /*#__PURE__*/_jsx(LazyApprovalNoteEditor, {
        "data-test-id": `product-approvals-approval-note-${currentOperation}`,
        placeholder: noteIsRequired ? unescapedText('productApprovals.approvalModalMessagePlaceholder') : unescapedText('productApprovals.approvalModalMessagePlaceholderOptional'),
        onChangeNote: onChangeNote,
        charLimit: MAX_APPROVAL_NOTE_LENGTH,
        onOverLimit: onNoteOverLimit
      }) : /*#__PURE__*/_jsx(TextArea, {
        role: "textbox",
        "data-test-id": `product-approvals-approval-note-${currentOperation}`,
        rows: 4,
        placeholder: unescapedText(noteIsRequired ? 'productApprovals.approvalModalMessagePlaceholder' : 'productApprovals.approvalModalMessagePlaceholderOptional'),
        value: note,
        onChange: onChangeNoteEvent
      })
    })]
  });
};